import { ParseMapError } from "../../errors";
import { MAP_THEMES } from "../../render/mapThemes";
import type { BuildingTileKind, GameMap, MapTheme, Tile, TileKind, TileOwner } from "../types";
import { isBuildingTile } from "../types";
import { parseStartingUnits, serializeStartingUnits } from "./startingUnitsJson";
import {
  parseCaptureCityTarget,
  parseVictoryConditions,
  shouldSerializeVictoryConditions,
} from "./victoryFields";

const TERRAIN_BY_CODE: Record<string, TileKind> = {
  W: "sea",
  P: "plain",
  F: "forest",
  M: "mountain",
  R: "road",
  E: "dropZone",
};

const CODE_BY_TERRAIN: Record<TileKind, string> = {
  sea: "W",
  plain: "P",
  forest: "F",
  mountain: "M",
  road: "R",
  dropZone: "E",
  city: "C",
  barracks: "B",
  factory: "G",
  base: "S",
  airport: "A",
  monument: "O",
  cannon: "N",
};

const BUILDING_BY_CODE: Record<string, BuildingTileKind> = {
  C: "city",
  B: "barracks",
  G: "factory",
  S: "base",
  A: "airport",
  O: "monument",
  N: "cannon",
};

const BUILDING_CELL_RE = /^([CBGASON]):(p1|p2|neutral)$/;
const MAP_THEME_SET = new Set<MapTheme>(MAP_THEMES);

export type MapDefinition = Omit<GameMap, "id">;

function parseTileCell(cell: unknown): Tile {
  if (typeof cell === "string") {
    const terrain = TERRAIN_BY_CODE[cell];
    if (terrain) {
      return { kind: terrain };
    }
    const buildingMatch = BUILDING_CELL_RE.exec(cell);
    if (buildingMatch) {
      const kind = BUILDING_BY_CODE[buildingMatch[1]!]!;
      const owner = buildingMatch[2]! as TileOwner;
      return { kind, owner };
    }
    throw new ParseMapError(`Invalid tile shorthand: ${cell}`);
  }

  if (typeof cell === "object" && cell !== null && "kind" in cell) {
    const tile = cell as {
      kind: TileKind;
      owner?: TileOwner;
    };
    if (isBuildingTile(tile.kind)) {
      return { kind: tile.kind, owner: tile.owner ?? "neutral" };
    }
    return { kind: tile.kind };
  }

  throw new ParseMapError(`Invalid tile: ${JSON.stringify(cell)}`);
}

function serializeTile(tile: Tile): string {
  if (isBuildingTile(tile.kind)) {
    return `${CODE_BY_TERRAIN[tile.kind]}:${tile.owner ?? "neutral"}`;
  }
  return CODE_BY_TERRAIN[tile.kind];
}

function isTileRow(row: unknown): row is unknown[] {
  return Array.isArray(row);
}

function parseTilesGrid({
  tiles,
  width,
  height,
}: {
  tiles: unknown[];
  width: number;
  height: number;
}): Tile[] {
  if (tiles.length === 0) {
    throw new ParseMapError("Map JSON tiles must not be empty");
  }

  if (isTileRow(tiles[0])) {
    if (tiles.length !== height) {
      throw new ParseMapError(`Expected ${height} tile rows, got ${tiles.length}`);
    }
    const parsed: Tile[] = [];
    for (let y = 0; y < tiles.length; y++) {
      const row = tiles[y];
      if (!isTileRow(row)) {
        throw new ParseMapError(`Invalid tile row at y=${y}`);
      }
      if (row.length !== width) {
        throw new ParseMapError(`Row ${y} has ${row.length} tiles, expected ${width}`);
      }
      parsed.push(...row.map(parseTileCell));
    }
    return parsed;
  }

  const parsed = tiles.map(parseTileCell);
  if (parsed.length !== width * height) {
    throw new ParseMapError(`Expected ${width * height} tiles, got ${parsed.length}`);
  }
  return parsed;
}

function serializeTilesGrid(definition: MapDefinition): string[][] {
  const { width, height, tiles } = definition;
  const rows: string[][] = [];
  for (let y = 0; y < height; y++) {
    const row: string[] = [];
    for (let x = 0; x < width; x++) {
      row.push(serializeTile(tiles[y * width + x]!));
    }
    rows.push(row);
  }
  return rows;
}

export function parseMapDefinition(json: string): MapDefinition {
  const parsed: unknown = JSON.parse(json);
  if (typeof parsed !== "object" || parsed === null) {
    throw new ParseMapError("Map JSON must be an object");
  }

  const record = parsed as Record<string, unknown>;
  const { width, height, theme, tiles, startingUnits, victoryConditions, captureCityTarget } =
    record;
  if (typeof width !== "number" || typeof height !== "number") {
    throw new ParseMapError("Map JSON must include numeric width and height");
  }
  if (!Array.isArray(tiles)) {
    throw new ParseMapError("Map JSON must include a tiles array");
  }
  if (!Array.isArray(startingUnits)) {
    throw new ParseMapError("Map JSON must include a startingUnits array");
  }
  if (theme !== undefined && (typeof theme !== "string" || !MAP_THEME_SET.has(theme as MapTheme))) {
    throw new ParseMapError(`Invalid map theme: ${JSON.stringify(theme)}`);
  }

  const parsedVictoryConditions = parseVictoryConditions(victoryConditions);
  const parsedCaptureCityTarget = parseCaptureCityTarget({
    value: captureCityTarget,
    victoryConditions: parsedVictoryConditions,
  });

  const parsedTiles = parseTilesGrid({ tiles, width, height });
  const parsedUnits = parseStartingUnits(startingUnits);

  return {
    width,
    height,
    ...(theme !== undefined ? { theme: theme as MapTheme } : {}),
    victoryConditions: parsedVictoryConditions,
    ...(parsedCaptureCityTarget !== undefined
      ? { captureCityTarget: parsedCaptureCityTarget }
      : {}),
    tiles: parsedTiles,
    startingUnits: parsedUnits,
  };
}

export function serializeMapDefinition(definition: MapDefinition): string {
  const payload = {
    width: definition.width,
    height: definition.height,
    ...(definition.theme !== undefined ? { theme: definition.theme } : {}),
    ...(shouldSerializeVictoryConditions(definition.victoryConditions)
      ? { victoryConditions: definition.victoryConditions ?? ["rout"] }
      : {}),
    ...(definition.captureCityTarget !== undefined
      ? { captureCityTarget: definition.captureCityTarget }
      : {}),
    tiles: serializeTilesGrid(definition),
    startingUnits: serializeStartingUnits(definition.startingUnits),
  };
  return `${JSON.stringify(payload, null, 2)}\n`;
}
